import { useState, useRef, useEffect, useContext } from 'react';
import { Box, IconButton, Slider } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import { secondsToHms } from 'src/utilities/formatTime';
import { ChatAudioContext } from '../context/ChatAudioContext';

const AudioMessagePlayer = ({ src, ...props }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(null);
  const audioRef = useRef(null);
  const { currentAudio, setCurrentAudio } = useContext(ChatAudioContext);

  const togglePlay = () => {
    if (isPlaying && audioRef.current) {
      audioRef.current.pause();
      setCurrentAudio(null);
    } else {
      audioRef.current.play();
      setCurrentAudio(audioRef.current.src);

      audioRef.current.onended = () => {
        setCurrentAudio(null);
        setIsPlaying(false);
        audioRef.current.currentTime = 0;
      };
    }
    setIsPlaying(!isPlaying);
  };

  useEffect(() => {
    const audioElement = audioRef.current;

    const onTimeUpdate = () => {
      setCurrentTime(audioElement.currentTime);
    };

    const onDurationChange = () => {
      setDuration(audioElement.duration);
    };

    const onLoadedMetadata = () => {
      setDuration(audioElement.duration);
    };

    audioElement.addEventListener('timeupdate', onTimeUpdate);
    audioElement.addEventListener('durationchange', onDurationChange);
    audioElement.addEventListener('loadedmetadata', onLoadedMetadata);

    return () => {
      audioElement.removeEventListener('timeupdate', onTimeUpdate);
      audioElement.removeEventListener('durationchange', onDurationChange);
      audioElement.removeEventListener('loadedmetadata', onLoadedMetadata);
    };
  }, []);

  useEffect(() => {
    if (!currentAudio) return;

    if (currentAudio !== audioRef.current.src) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  }, [currentAudio]);

  return (
    <>
      <Box
        sx={{
          width: '30vw',
          minWidth: '150px',
          maxWidth: '250px',
          display: 'flex',
          justifyContent: 'space-evenly',
          alignItems: 'center',
          gap: 1.5,
        }}
      >
        <IconButton
          color="primary"
          aria-label="play/pause"
          onClick={togglePlay}
          disabled={duration === null}
          size="small"
        >
          {isPlaying ? (
            <PauseIcon sx={{ height: 20, width: 20 }} />
          ) : (
            <PlayArrowIcon sx={{ height: 20, width: 20 }} />
          )}
        </IconButton>
        <Slider
          size="small"
          min={0}
          max={duration}
          value={currentTime}
          disabled={duration === null}
          onChange={(_, value) => {
            audioRef.current.currentTime = value;
            setCurrentTime(value);
          }}
          sx={{
            height: '3px',
          }}
        />
        <span>
          {secondsToHms(
            !isPlaying && duration
              ? duration?.toFixed(0)
              : currentTime.toFixed(0)
          )}
        </span>
      </Box>
      <audio ref={audioRef} src={src} {...props} />
    </>
  );
};

export default AudioMessagePlayer;
